import React from 'react';
import PropTypes from 'prop-types';

import { useArrayText } from '../../hooks/useArrayText';
import FsTooltip from '../FsWrappers/FsTooltip/FsTooltip';
import { Text } from '../TextComponents';

const OverflowArrayTextDisplayWithTooltip = ({ value, renderItem, additionalLabelWidth, maxTooltipItems }) => {
  const getLabel = React.useCallback((item) => item, []);

  const { containerRef, displayedItems, remainingCount } = useArrayText({
    array: value,
    renderLabel: getLabel,
    labelAdditionalWidth: additionalLabelWidth,
  });

  const remainingCountString = remainingCount > 0 ? ` +${remainingCount}` : '';
  const text = displayedItems.join(', ');

  // The tooltip reveals the items truncated inline, so callers that expect long lists can pass
  // maxTooltipItems to keep it from becoming a wall of text; without it every item is listed.
  // The cap can never hide something the user is already reading: `displayedItems` is decided by the
  // measured container width, so floor the cap at what rendered inline — otherwise a cell wide
  // enough for more than maxTooltipItems chips would get a tooltip that both drops visible items and
  // invents a `+N` the inline badge does not show.
  const tooltipCap = Math.max(maxTooltipItems ?? Infinity, displayedItems.length);
  const tooltipTitle =
    value?.length > tooltipCap
      ? `${value.slice(0, tooltipCap).join(', ')} +${value.length - tooltipCap}`
      : (value?.join(', ') ?? '');

  return (
    <FsTooltip arrow title={tooltipTitle} disabled={!value}>
      <div ref={containerRef}>
        <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR} className="flex flex-nowrap items-center">
          {renderItem ? displayedItems.map(renderItem) : text}
          {remainingCountString}
        </Text>
      </div>
    </FsTooltip>
  );
};

OverflowArrayTextDisplayWithTooltip.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string),
  renderItem: PropTypes.func,
  additionalLabelWidth: PropTypes.number,
  maxTooltipItems: PropTypes.number,
};

export default OverflowArrayTextDisplayWithTooltip;
